<template>
  <div class="viewport" ref="viewport" :controlsBg="controlsBg">
    <div class="main-nav-wrapper">
      <main-nav></main-nav>
    </div>
    <div ref="canvas" class="canvas">
      <div class="canvas__content">
        <top-bar
          :breadcrumb="breadcrumb"
          :hasSettings="hasSettings">
          <slot name="settings" slot="settings"></slot>
        </top-bar>
        <controls-bar
          v-if=hasControlsBar
          :hasFilterLeft="hasFilterLeft"
          :hasFilterRight="hasFilterRight"
          :disabledDate="disabledDate"
          :controlsBg="controlsBg"
          :filterTitleLeft="filterTitleLeft"
          :filterTitleRight="filterTitleRight"
          :filterDateIdLeft="filterDateIdLeft"
          :filterDateIdRight="filterDateIdRight"
          :filterStartDateLeft="filterStartDateLeft"
          :filterStartDateRight="filterStartDateRight"
          :filterModeSelected="filterModeSelected"
          :filterModeTitle="filterModeTitle"
          :customControls="customControls">
            <slot
              name="custom-controls-top"
              slot="custom-controls-top">
            </slot>
          </controls-bar>
        <div :class="hasControlsBar ? 'wrapper' : 'wrapper-without-controls-bar'">
          <slot></slot>
          <div v-if="showPoweredBy" class="powered-by-wrapper">
            <powered-by />
          </div>
        </div>
      </div>
    </div>
    <div class="overlay" @click="closeNavs()"></div>
    <div class="secondary-nav-wrapper">
      <slot name="secondary-nav"></slot>
    </div>
    <chatbot />
  </div>
</template>

<script>
import { eventBus } from '@/main';
import MainNav from '@/components/shared/MainNav';
import TopBar from '@/components/shared/TopBar';
import ControlsBar from '@/components/shared/ControlsBar';
import PoweredBy from '@/components/shared/logos/PoweredBy';
import Chatbot from '@/components/shared/chatbot/Chatbot';

export default {
  components: {
    TopBar,
    MainNav,
    ControlsBar,
    PoweredBy,
    Chatbot
  },
  props: {
    hasSettings: {
      type: Boolean,
      default: false
    },
    hasControlsBar: {
      type: Boolean,
      default: true
    },
    controlsBg: {
      type: String,
      default: 'dark'
    },
    hasFilterLeft: {
      type: Boolean,
      default: false
    },
    hasFilterRight: {
      type: Boolean,
      default: false
    },
    breadcrumb: {
      type: Array,
      default: () => []
    },
    filterStartDateLeft: {
      type: String,
      default: ''
    },
    filterStartDateRight: {
      type: String,
      default: ''
    },
    filterDateIdLeft: {
      type: String,
      default: ''
    },
    filterDateIdRight: {
      type: String,
      default: ''
    },
    filterTitleLeft: {
      type: String,
      default: ''
    },
    filterTitleRight: {
      type: String,
      default: ''
    },
    filterModeSelected: {
      type: String,
      default: ''
    },
    filterModeTitle: {
      type: String,
      default: ''
    },
    customControls: {
      type: Boolean,
      default: false
    },
    disabledDate: {
      type: Function,
      default: () => {
        return false;
      }
    },
    showPoweredBy: {
      type: Boolean,
      default: true
    }
  },
  created () {
    eventBus.$on('navOpened', () => {
      if (this.$refs.viewport) {
        this.$refs.viewport.classList.add('is-navOpen');
        this.$refs.viewport.classList.add('is-offcanvas-open');
      }
    });
    eventBus.$on('navClosed', () => {
      if (this.$refs.viewport) {
        this.$refs.viewport.classList.remove('is-navOpen');
        this.$refs.viewport.classList.remove('is-offcanvas-open');
      }
    });
    eventBus.$on('secondaryNavOpened', () => {
      if (this.$refs.viewport) {
        this.$refs.viewport.classList.add('is-secondaryNavOpen');
        this.$refs.viewport.classList.add('is-offcanvas-open');
      }
    });
    eventBus.$on('secondaryNavClosed', () => {
      if (this.$refs.viewport) {
        this.$refs.viewport.classList.remove('is-secondaryNavOpen');
        this.$refs.viewport.classList.remove('is-offcanvas-open');
      }
    });
    eventBus.$on('closeNavs', () => {
      if (this.$refs.viewport) {
        this.$refs.viewport.classList.remove('is-secondaryNavOpen');
        this.$refs.viewport.classList.remove('is-navOpen');
        this.$refs.viewport.classList.remove('is-offcanvas-open');
      }
    });
    document.addEventListener('keydown', event => {
      if (event.keyCode === 27) {
        eventBus.$emit('closeNavs');
      }
    });
  },
  methods: {
    closeNavs () {
      eventBus.$emit('closeNavs');
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../stylesheets/variables.scss";

.viewport {
  overflow-x: hidden;
  &.is-offcanvas-open {
    overflow-y: hidden;
  }
}

.canvas,
.main-nav-wrapper,
.secondary-nav-wrapper {
  transition: 0.3s;
}

.main-nav-wrapper {
  width: $navWidth;
  position: fixed;
  top: 0;
  bottom: 0;
  left: -$navWidth;
  z-index: 1;
}

.secondary-nav-wrapper {
  width: $navWidth;
  position: fixed;
  top: 0;
  bottom: 0;
  right: -$navWidth;
}

.is-navOpen {
  .main-nav-wrapper,
  .canvas {
    transform: translateX($navWidth);
  }
  .canvas__content {
    pointer-events: none;
    position: relative;
  }
  .overlay {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: $black-trans2;
    .canvas-light & {
      background-color: $white-trans2;
    }
  }
}
.is-secondaryNavOpen {
  .secondary-nav-wrapper,
  .canvas {
    transform: translateX(-$navWidth);
  }
  .canvas__content {
    pointer-events: none;
    position: relative;
  }
  .overlay {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: $black-trans2;
    .canvas-light & {
      background-color: $white-trans2;
    }
  }
}

.powered-by-wrapper {
  position: absolute;
  right: $sp-margin;
  bottom: 0;
  svg {
    width: 150px;
    margin-left: auto;
    margin-right: $sp-margin;
    margin-bottom: $sp-margin-smaller;
    color: $black-trans6;
  }
}
</style>



// WEBPACK FOOTER //
// src/layouts/MainLayout.vue